import { Asset } from '#base/Asset.js';
import { AssetGroup, getGroupsContainingAsset, useGrouping } from '#base/AssetGroup.js';
import { Cancel, useCancel } from '#base/Canvas/Cancel.js';
import { ColorPicker } from '#base/Color.js';
import { DesignGridWrapper, DesignOption } from '#base/Designer/index.js';
import { ArtworkToolbar } from '#base/Designer/Toolbars/ArtworkToolbar.js';
import { ToolViewModes } from '#base/Designer/types.js';
import UploadedToolIcon from '#base/resources/icons/uploaded-tool.svg';
import Button from '#base/ui/Button.js';
import { Spinner } from '#base/ui/Spinner.js';
import { Upload } from '#base/ui/Upload.js';
import * as Ajax from '#base/util/ajax.js';
import { loadImage, convertSVGToPNG, uploadImageAsync, convertImageToPNGAsync } from '#base/util/image.js';
import {
  DEFAULT_COLOR,
  formatCurrency,
  MINIMUM_BITMAP_DPI,
  MINIMUM_BITMAP_SIZE_INCHES,
} from '@c2s-npm/plumlib';
import { vectorizeAsync } from '@c2s-npm/vectorize/vectorize.js';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { FaChevronLeft, FaMinus, FaPlus } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

let gNewUploadId = 0;

export const bitmapInfo = async (url) => {
  const img = await loadImage(url);
  const width = img.naturalWidth;
  const height = img.naturalHeight;

  const maxWidthInch = width / MINIMUM_BITMAP_DPI;
  const maxHeightInch = height / MINIMUM_BITMAP_DPI;

  return {
    width,
    height,
    maxWidthInch,
    maxHeightInch,
  };
};

const getVectorizedOptionsPromise = (file, graphicType, config) => {
  return fetch(file.url)
    .then((r) => r.arrayBuffer())
    .then(async (data) => {
      const options = (await vectorizeAsync(data, 'source', graphicType)).reduce(
        (a, { name, svg, bitmap }) => {
          const mimetype = svg ? 'image/svg+xml' : file.mimetype;
          const imgData = svg ? svg : bitmap;
          const imgBase64 = btoa(imgData.reduce((data, byte) => data + String.fromCharCode(byte), ''));
          const imageURI = 'data:' + mimetype + ';base64,' + imgBase64;
          const vectorized = !!svg;

          const option = {
            ...file,  // may have extra fields, such as deferredDataUrl
            url: imageURI,
            mimetype: mimetype,
            name,
            vectorized,
          };
          if (bitmap) {
            option.promise = new Promise((resolve) => {
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              img.src = imageURI;
              img.onload = async () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
              };
            });
          }

          const first = a[0];
          if (!first) {
            a.push(option);
          } else if (imageURI.length / first.url.length > 0.3 && !a.find((o) => o.url === imageURI)) {
            a.push(option);
          }

          return a;
        },
        []
      );

      const promises = options.map((o) => o.promise);
      return Promise.all(promises).then((values) => {
        for (let i = 0; i < values.length; i++) {
          if (values[i]) {
            options[i].bitmapInfo = values[i];
          }
        }

        // Remove bitmaps that are too small
        let hasSmallBitmap = false;
        for (let i = values.length - 1; i >= 0; --i) {
          if (options[i].bitmapInfo) {
            const maxWidthInch = options[i].bitmapInfo.width / MINIMUM_BITMAP_DPI;
            const maxHeightInch = options[i].bitmapInfo.height / MINIMUM_BITMAP_DPI;
            if (maxWidthInch <= MINIMUM_BITMAP_SIZE_INCHES && maxHeightInch <= MINIMUM_BITMAP_SIZE_INCHES) {
              hasSmallBitmap = true;
              options[i].disabled = true;
            } else if (
              config &&
              (maxWidthInch * 25.4 < config.print.width || maxHeightInch * 25.4 < config.print.height)
            ) {
              options[i].bitmapInfo.maxWidthInch = maxWidthInch;
              options[i].bitmapInfo.maxHeightInch = maxHeightInch;
            }
          }
        }

        const sorted = options
          .filter((o) => o.name === 'Color')
          .concat(options.filter((o) => o.name !== 'Color'));
        return { sorted, hasSmallBitmap };
      });
    });
};

const _UploadImage = ({ width = 100, height = 100, onReceived }) => {
  return <Upload style={{ width: width, height: height }} onReceived={onReceived} />;
};

const RenderImage = ({
  asset,
  selected,
  label,
  tooltip,
  src,
  size = 'md',
  onClick,
  hasPremium,
  hasOtherElements,
  restrictionInfo,
  isUpload = false,
}) => {
  var imageURI = src;
  if (typeof src == 'object') {
    let svgBase64 = btoa(src.svg.reduce((data, byte) => data + String.fromCharCode(byte), ''));
    imageURI = 'data:image/svg+xml;base64,' + svgBase64;
  }

  const backgroundColor = asset?.attributes?.bg;

  return (
    <Asset
      className='t-flex t-flex-col t-gap-2'
      {...{ asset, hasPremium, hasOtherElements, restrictionInfo, tooltip }}>
      <div
        className={classNames(
          `t-flex t-max-h-full t-max-w-full t-cursor-pointer t-rounded-xl t-border t-overflow-hidden t-bg-white`,
          {
            't-border-primary': selected,
            't-border-neutrals-light-grey': !selected,
          },
          {
            't-h-[100px] t-w-[100px]': size === 'md',
            't-h-[150px] t-w-[150px]': size === 'lg',
          },
          {
            't-opacity-50': !onClick,
          },
          {
            'design-bg-checkerboard': isUpload,
          }
        )}
        style={{ backgroundColor }}
        onClick={onClick}>
        <div
          className={classNames(
            `t-group t-flex t-h-full t-w-full t-flex-col t-items-center t-justify-center t-gap-2 t-transition t-duration-200 hover:t-backdrop-filter hover:t-backdrop-brightness-90`,
            {
              't-p-3': !label,
              't-p-1': !!label,
            },
            {
              't-pb-8': !!asset?.fee,
            }
          )}>
          <div
            className={classNames({
              't-max-h-full t-max-w-full': !label,
              't-h-[calc(100%-24px)] t-w-[calc(100%-24px)]': label,
            })}>
            <img
              src={imageURI}
              className={classNames('t-h-full t-w-full t-object-contain', {
                't-filter t-drop-shadow': isUpload,
              })}
            />
          </div>
          {label && (
            <div className='t-block t-whitespace-nowrap t-text-center t-text-sm t-font-normal t-text-muted'>
              {label}
            </div>
          )}
        </div>
      </div>
    </Asset>
  );
};

export const ImageSelect = ({
  artwork,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  graphicType,
  restrictionInfo,
  assetGroupFilter,
  autoSelectGroup = true,
  defaultColor,
  defaultSelectedElement = null,
}) => {
  const [selectedImage, selectImage] = useState(
    selected
      ? artwork.find((f) => f.key === selected.get('data'))
      : defaultSelectedElement
        ? artwork.find((a) => a.key === defaultSelectedElement)
        : null
  );
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ maxWidth: 1023 });
  const [selectedColor, setColor] = useState(selected?.get?.('color') || defaultColor || DEFAULT_COLOR);
  const [currentColor, setCurrentColor] = useState(selectedColor);
  const canColor =
    !selectedImage?.attributes?.graphicType ||
    selectedImage?.attributes?.graphicType?.includes?.(graphicType);
  const canRecolor =
    (canColor && typeof selectedImage?.attributes?.canRecolor === 'undefined') ||
    !!selectedImage?.attributes?.canRecolor;

  const [commit, undo, canRevert, isNew] = useCancel(selected, cancel, revert);

  useEffect(() => {
    selectedImage &&
      onSelect &&
      onSelect(
        Object.assign({
          elementType: 'artwork',
          assetId: selectedImage.key,
          data: selectedImage.key,
          color: canRecolor ? currentColor : null,
        })
      );
  }, [selectedImage, canRecolor, currentColor]);

  const [subGroups, assets, selectedGroup, selectGroup, hasPremiumElement, hasOtherElements] = useGrouping({
    groups,
    assets: artwork,
    autoSelectGroup: autoSelectGroup && selectedImage?.key,
    elements,
    assetGroupFilter,
    graphicTypeFilter: graphicType,
  });

  const isRootGroup = !selectedGroup || selectedGroup === 'root';
  const showPremiumWarning = (hasPremium === 'item' || hasOtherElements) && hasPremiumElement;

  const activeGroups = useMemo(
    () => (selectedImage ? getGroupsContainingAsset(groups, selectedImage.key) : groups),
    [groups, selectedImage]
  );

  const [mode, setMode] = useState(ToolViewModes.ASSET);

  const onCancelClick = () => {
    if (mode === ToolViewModes.ASSET) undo();
    else {
      setCurrentColor(selectedColor);
      setMode(ToolViewModes.ASSET);
    }
  };

  const onConfirmUpdate = () => {
    setColor(currentColor);
    setMode(ToolViewModes.ASSET);
  };

  return (
    <>
      {!isRootGroup && (
        <div
          className={classNames({
            't-relative t-top-[-33px] t-ml-[-40px] t-mt-[-80px] t-w-[calc(100%+80px)] t-bg-white': !isTablet,
            't-fixed t-left-0 t-right-0 t-top-0 t-z-10': isTablet,
          })}>
          <p
            className={classNames('t-mb-0 t-flex t-cursor-pointer t-items-center', {
              't-gap-4 t-px-4 t-pb-4 t-pt-8 t-text-lg lg:t-text-2xl': !isTablet,
              't-relative t-z-10 t-gap-3 t-border-b t-border-b-gray-300 t-bg-white t-px-[10px] t-py-3 t-text-xs':
                isTablet,
            })}
            onClick={() => selectGroup(selectedGroup?.parents?.[0] ?? null)}>
            <FaChevronLeft className='t-text-sm lg:t-text-2xl' color='var(--plum-brand-black)' />
            <span className='t-font-bold t-text-brand-black'>{selectedGroup.groupName}</span>
            {!!selectedGroup?.fee && (
              <span
                className={classNames('t-font-bold', {
                  't-text-brand-black': !selectedGroup?.usualRate,
                  't-text-onsale': !!selectedGroup?.usualRate,
                })}>
                {formatCurrency(selectedGroup?.fee)}{' '}
                {selectedGroup?.usualRate && (
                  <span className='t-text-brand-black t-font-normal t-line-through'>
                    {formatCurrency(selectedGroup.usualRate)}
                  </span>
                )}
              </span>
            )}
          </p>
        </div>
      )}

      {!!selectedImage && graphicType === 'color' && (
        <div className='t-relative -t-left-4 t-z-10 t-mx-auto -t-mt-7 t-mb-4 t-block t-w-[calc(100%+32px)] t-rounded-t-xl t-border-b t-border-neutrals-light-grey t-bg-white t-px-4 t-py-2 t-shadow-[0px_6px_6px_#00000029] lg:-t-left-10 lg:-t-mt-8 lg:t-w-[calc(100%+80px)] lg:t-px-10 lg:t-pb-4 lg:t-pt-3'>
          <ArtworkToolbar {...{ mode, setMode, graphicType, canRecolor }} />
        </div>
      )}
      <div
        className={classNames('t-mx-auto t-max-w-[360px]', {
          't-block': mode === ToolViewModes.ASSET,
          't-hidden': mode !== ToolViewModes.ASSET,
        })}>
        <div className='t-mx-auto t-max-w-[360px]'>
          {subGroups.length === 0 && assets.length === 0 && (
            <em className='t-font-bold t-not-italic t-text-muted'>Empty group</em>
          )}

          {showPremiumWarning && (
            <div className='t-text-left t-text-brand-red'>
              Premium assets cannot be used alongside other designs and text.
            </div>
          )}
          <DesignGridWrapper mode='grid' className='t-py-3' mobileMaxHeight='calc(100dvh - 320px)'>
            <AssetGroup
              groups={subGroups}
              {...{
                activeGroups,
                selectedGroup,
                selectGroup,
                hasElements: hasOtherElements,
                hasPremium,
                hasPremiumElement,
              }}
            />

            {assets.length > 0 && (
              <>
                {assets.map((image, i) => (
                  <RenderImage
                    key={i}
                    asset={image}
                    selected={selectedImage === image}
                    src={image.def.file}
                    onClick={() => selectImage(image)}
                    {...{ hasPremium, hasOtherElements, restrictionInfo }}
                  />
                ))}
              </>
            )}
          </DesignGridWrapper>
        </div>
      </div>

      {canRecolor && (
        <div
          className={classNames('t-mx-auto t-w-[360px]', {
            't-block': mode === ToolViewModes.COLOR && selectedImage,
            't-hidden': mode !== ToolViewModes.COLOR || !selectedImage,
          })}>
          <ColorPicker value={currentColor} onSelect={setCurrentColor} />
        </div>
      )}

      <div className='t-mt-5 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        {mode === ToolViewModes.ASSET && (
          <Cancel
            disabled={!selectedImage}
            hideButton={assets.length === 0}
            {...{ commit, undo, canRevert, isNew, label: 'Artwork' }}
          />
        )}

        {mode === ToolViewModes.COLOR && (
          <div className='t-flex t-items-center t-justify-center t-gap-3 t-text-center lg:t-flex-col'>
            <Button
              size={isMobile ? 'sm' : 'base'}
              variant='primary'
              fullWidth='all'
              onClick={onConfirmUpdate}>
              Update Color
            </Button>

            <Button size={isMobile ? 'sm' : 'base'} variant='link' onClick={onCancelClick}>
              Back
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export const CustomImageUpload = ({
  onSelect,
  uploadUrl,
  onUpload,
  cancel,
  uploadFee,
  uploaded,
  onUploadedClick,
  hasPremium,
  config,
  wantUploadsDeferred,
  mode,
}) => {
  const [commit, undo, canRevert, isNew] = useCancel(null, cancel, null);
  const [accordionOpen, setAccordionOpen] = useState(true);

  const handleUpload = async (file) => {
    onUpload && onUpload(file);

    const blob = await fetch(file.url).then((r) => r.blob());
    const reader = new FileReader();
    reader.onload = async ({ target: { result: dataUrl } }) => {
      let e = {
        ...file,  // may have extra fields, such as deferredDataUrl
        elementType: 'uploaded',
        url: dataUrl,
        fileId: file.id,
        mimetype: file.mimetype,
      }
      if (!e.mimetype.startsWith("image/svg")) {
        e.bitmapInfo = await bitmapInfo(dataUrl);
      }
      onSelect(e);
    };
    reader.readAsDataURL(blob);
  };

  const toggleAccordion = () => setAccordionOpen((prevState) => !prevState);

  return (
    <>
      <div className='t-mb-4'>
        <p className='t-mb-0 t-text-sm t-font-normal'>
          We accept vector files (SVG, EPS, AI) or PNG with transparent backgrounds.
        </p>
      </div>
      <Uploader {...{ uploadUrl, onUploaded: handleUpload, config, wantUploadsDeferred }} />

      {uploaded && uploaded.length > 0 && (
        <div className='t-my-3'>
          <p className='t-mb-2 t-text-sm t-font-normal'>Browse your previously uploaded designs</p>
          <DesignOption
            iconUrl={UploadedToolIcon}
            text='Your Designs'
            subtext={
              uploadFee?.rate && (
                <span
                  className={classNames({
                    't-text-brand-black': !uploadFee?.usualRate,
                    't-text-onsale t-font-bold': !!uploadFee?.usualRate,
                  })}>
                  {uploadFee?.rate}{' '}
                  {uploadFee?.usualRate && (
                    <span className='t-text-brand-black t-font-normal t-line-through'>
                      {uploadFee.usualRate}
                    </span>
                  )}
                </span>
              )
            }
            onClick={onUploadedClick}
            disabled={!!hasPremium}
            mode='horizontal'
          />
        </div>
      )}

      <div
        className='t-relative t-z-10 -t-mx-4 t-mt-4 t-flex t-w-screen t-cursor-pointer t-items-center t-justify-between t-border t-border-neutrals-light-grey t-p-3 t-text-sm t-font-bold lg:-t-mx-10 lg:t-w-[calc(100%+80px)]'
        onClick={toggleAccordion}>
        <span>Design Upload Terms & FAQs</span>
        {accordionOpen ? (
          <FaMinus size={14} className='t-text-body' />
        ) : (
          <FaPlus size={14} className='t-text-body' />
        )}
      </div>
      <div
        className={classNames(
          't-relative t-z-0 t-overflow-hidden t-font-normal t-transition-all t-duration-500 [&_p]:t-text-sm [&_p]:t-leading-5',
          {
            't-max-h-0': !accordionOpen,
            't-max-h-[360px] t-overflow-y-auto t-py-3': accordionOpen,
          }
        )}>
        <DesignGridWrapper
          desktopMaxHeight='360px'
          mobileMaxHeight={uploaded && uploaded.length > 0 ? 'calc(100dvh - 650px)' : 'calc(100dvh - 520px)'}>
          <div className='t-py-3'>
            <ol className='t-mb-3 t-list-decimal t-pl-3 t-font-normal [&>li]:t-mb-2 [&>li]:t-text-sm'>
              <li>Make sure your design is crisp and clear (larger files, higher resolution). </li>
              <li>Solid Colors work better than Gradients.</li>
              <li>Move your design to a transparent or white background.</li>
            </ol>
            <p className='t-mb-3'>
              By uploading a design you are confirming that you either own or are an authorized licensee of
              the content that you have uploaded to RTIC (e.g., mark, logo, slogan, image, or text), and that
              you have all rights to complete this purchase, and to use, display, and resell the content.
            </p>
            <p className='t-mb-3'>
              Customer has granted RTIC a fully paid, worldwide, non-exclusive right and license to use your
              owned or licensed copyrights, logos, insignia, trademarks, and other artistic designs contained
              in the specific materials submitted to RTIC (the “Content”) for the purpose of providing the
              custom product and for marketing and promotional purposes. RTIC will retain all rights, title
              and interest in and to the final custom designed product.
            </p>
            <p className='t-mb-3'>
              All uploaded designs will be reviewed and RTIC Outdoors has discretion to not produce any
              products that do not meet the company’s standards for acceptable use.
            </p>
          </div>
        </DesignGridWrapper>
      </div>
      <div className='t-mt-5 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        <Cancel {...{ commit, undo, canRevert, isNew, label: 'Design' }} />
      </div>
    </>
  );
};

export const CustomImageSelect = ({
  uploaded = [],
  onSelect,
  selected,
  cancel,
  revert,
  uploadUrl,
  uploadFee,
  config,
  graphicType,
  defaultColor,
  mode,
}) => {
  const kVStateNotStarted = 1;
  const kVStateWorking = 2;
  const kVStateDone = 3;

  const [vectorizationState, setVectorizationState] = useState(kVStateNotStarted);

  const [options, setOptions] = useState([]);
  const [selectedFile, selectFile] = useState(null);
  const [selectedOption, _selectOption] = useState(null);
  const [bitmapIsTooSmall, setBitmapIsTooSmall] = useState(false);
  const [color, setColor] = useState(selected?.get?.('color') || defaultColor || DEFAULT_COLOR);
  const [doingConversionToPNG, setDoingConversionToPNG] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const selectOption = (opts) => {
    // SVGs can have their own colors, but vectorized options do not have an intrinsic color
    _selectOption(opts); // so no-color is not a valid option
    if (opts && opts.length > 0 && color == null) {
      setColor(defaultColor || DEFAULT_COLOR);
    }
  };

  const selectedFileId = selected && selected.get('fileId');

  const [commit, undo, canRevert, isNew] = useCancel(selected, cancel, revert);

  useEffect(() => {
    if (!selected || !['uploaded','file'].includes(selected.get('elementType'))) return;
    // if (graphicType === 'color') return;

    if (selected.get('vectorized')) {
      // const source = uploaded.find(u => u.fileId === selected.get('fileId'));
      // source && selectFile(source);
      // TODO should request vectors and select option?
    } else {
      requestRefinement(selected.toJS());
    }
  }, [selected, graphicType]);

  useEffect(() => {
    const updateIfNecessary = async () => {
      // We want to update if the selected file is different or the color changed for a non-vectorized image.
      // Vectorized images use the alternate UI, and we do not want to reset this. Problem: checking for
      // empty options (either nothing selected or an already-vectored image) makes it impossible to select
      // a different file if you've selected a vectorizable file. But the UI does not actually support that,
      // so it's not a problem, at least currently.
      if (selectedFile && (options?.length || 0) === 0 && !doingConversionToPNG) {
        setDoingConversionToPNG(true);
        const blob = await fetch(selectedFile.url).then((r) => r.blob());
        const isSVG = selectedFile.mimetype.startsWith('image/svg')

        const toSelect = {
          ...selectedFile,  // note that selectedFile may keys we do not know about, such as deferredDataUrl
          elementType: 'uploaded',
          fileId: selectedFile.id,
          vectorized: false,
          bitmapInfo: await bitmapInfo(selectedFile.url),
        };
        
        const reader = new FileReader();
        reader.onload = async ({ target: { result: dataUrl } }) => {
          if (isSVG) {
            // This will only happen if there is a pre-conversion SVG that is still in the user's upload library
            // which they selected.
            let png = await convertSVGToPNG(new File([blob], selectedFile.filename, { type: selectedFile.mimetype }), config);
            const filename = toSelect.filename.replace(/[.]svg$/, '') + '.png';
            const link = {filename, parentId: toSelect.fileId, linkType: 'png conversion'};
            const converted = new File([png.blob], filename);
            uploadImageAsync(uploadUrl, converted, {links: [link]})
              .then(async (json) => {
                onSelect({
                  ...toSelect,
                  fileId: json[0].id,
                  filename: filename,
                  mimetype: json[0].mimetype,
                  url: png.url,
                  bitmapInfo: await bitmapInfo(png.url),
                });
              }).catch((reason) => {
                setErrorMessage(
                    'Internal error: Could not process this SVG, please try again later or select a different image.'
                );
              });
          } else {
            onSelect({...toSelect, url: dataUrl});
          }
          setDoingConversionToPNG(false);
        };
        reader.readAsDataURL(blob);
      }
    };
    updateIfNecessary();
  }, [selectedFile]);

  useEffect(() => {
    if (selectedOption) {
      onSelect({
        ...selectedOption,
        elementType: 'uploaded',
        vectorized: true,
        vectorizeMethod: selectedOption?.name,
        color: !!selectedOption?.vectorized ? color : null,
      });
    }
  }, [selectedOption, color]);

  const requestRefinement = (file) => {
    // don't bother attempting to rasterize vectors
    if (file.mimetype.startsWith('image/svg')) return;

    setVectorizationState(kVStateWorking);
    setErrorMessage(null);
    getVectorizedOptionsPromise(file, graphicType, config)
      .then((results) => {
        setBitmapIsTooSmall(results.hasSmallBitmap);
        setOptions([...new Set(results.sorted)]);
        selectOption(results.sorted[0]);
        setVectorizationState(kVStateDone);
      })
      .catch((e) => {
        console.error('Error vectorizing', e);
        const isSvgError = file.mimetype.startsWith('image/svg'); // we didn't compile GraphicsMagick with SVG support
        if (isSvgError && graphicType !== 'engraved') {
          let colorFile = { ...file, color: null };
          selectFile(colorFile);
          setColor(null);
        } else {
          setErrorMessage(
            'We could not process this file, it may be corrupted or an image format we do not currently support.'
          );
          setOptions([]);
        }
        setVectorizationState(kVStateDone);
        // TODO something better than this
        alert('We could not process this file, it may be corrupted or an image format we do not currently support.')
        revert();
      });
  };

  const isRefining = vectorizationState === kVStateWorking;
  const isRefinable = options.length > 0 || isRefining;

  return (
    <>
      {errorMessage && (
        <div className='t-text-center' style={{ color: 'red' }}>
          {errorMessage}
        </div>
      )}
      {isRefinable && (
        <>
          {!isRefining && (
            <>
              {mode !== 'pro' && <p className='t-text-sm t-font-normal'>
                Based on the design you uploaded, we optimized your file and generated some options for you to
                choose from.
              </p>}
              {mode === 'pro' && <p>
                Vectorized image options. 
                {graphicType === 'color' && <span>Options other than the original can be colorized with a single color.</span>}
              </p>}
            </>
          )}
          {isRefining && (
            <p className='t-flex t-items-center t-justify-center t-text-center'>
              <Spinner className='md'>Optimizing file...</Spinner>
            </p>
          )}
          <DesignGridWrapper mode='grid' className='t-py-3'>
            {options.map((o, i) => {
              return (
                <RenderImage
                  key={i}
                  selected={selectedOption === o}
                  src={o.url}
                  onClick={o.disabled ? null : () => selectOption(o)}
                  tooltip={o.name}
                  label={mode === 'pro' ? (o.name === 'Color' ? 'Original' : o.name) : null}
                />
              );
            })}
          </DesignGridWrapper>
          {!isRefining && graphicType !== 'engraved' && bitmapIsTooSmall && (
            <p className='t-my-3 t-items-center t-rounded t-bg-[#f8ac59] t-p-5 t-text-sm t-text-white'>
              Image is too small to be used as a full-color image. We require at least {MINIMUM_BITMAP_DPI}{' '}
              pixels per inch for a full-color image.
            </p>
          )}
          {!isRefining &&
            graphicType !== 'engraved' &&
            !selectedOption?.vectorized && 
            (selectedOption?.bitmapInfo?.maxWidthInch ||
              0 > 0 ||
              selectedOption?.bitmapInfo?.maxHeightInch ||
              0 > 0) && (
              <div className='t-my-3 t-items-center t-rounded t-bg-[#f8ac59] t-p-5 t-text-sm t-text-white'>
                <p className='t-my-0 t-text-sm'>Your original image can only printed at a maximum size of:</p>
                <p className='t-my-3 t-text-center'>
                  {selectedOption.bitmapInfo.maxWidthInch.toFixed(2)}" x{' '}
                  {selectedOption.bitmapInfo.maxHeightInch.toFixed(2)}"
                </p>
                <p className='t-my-0 t-text-sm'>
                  For a larger size please use one of the optimized options, or upload a larger image. We
                  recommend full-color images have at least 300 pixels per printed inch.
                </p>
              </div>
            )}
          {!isRefining &&
            graphicType === 'color' &&
            !!selectedOption?.vectorized && (
              <div className='t-flex t-items-center t-justify-end t-gap-3 t-py-3 t-text-right'>
                <ColorPicker value={color} onSelect={setColor} {...{mode}} />
              </div>
            )}
        </>
      )}
      {!isRefinable && uploaded && uploaded.length > 0 && (
        <div className='t-mx-auto t-max-w-[360px] lg:t-max-w-full'>
          <DesignGridWrapper mode='grid' className='t-py-3'>
            {uploaded
              .filter((img, i) => img != null && i != null)
              .map((img, i) => {
                // during SVG -> PNG conversion we get nulls in 'uploaded'
                return (
                  <RenderImage
                    key={i}
                    tooltip={img.filename}
                    selected={selectedFileId === img.id}
                    src={img.url}
                    onClick={() => selectFile(img)}
                    isUpload={true}
                  />
                );
              })}
          </DesignGridWrapper>
        </div>
      )}
      <div className='t-mt-5 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        {!!uploadFee && (
          <p className='t-mb-0 t-mt-4 t-text-center t-text-xs t-font-normal t-text-body lg:t-text-sm'>
            A{' '}
            {uploadFee?.rate && (
              <strong
                className={classNames('t-font-bold', {
                  't-text-onsale': !!uploadFee?.usualRate,
                })}>
                {uploadFee?.rate}
              </strong>
            )}{' '}
            fee will be applied for each unique design.
          </p>
        )}
        <Cancel {...{ commit, undo, canRevert, isNew, label: 'Design' }} />
      </div>
    </>
  );
};


export const Uploader = ({ uploadUrl, onUploaded, config, wantUploadsDeferred }) => {
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const setMessageAsync = async (...e) => setMessage(...e);

  const handleUpload = async (e) => {
    const { files } = e.target;
    let file = files && files.length > 0 ? files[0] : null;

    if (file) {
      setMessageAsync('Processing file...');
      setProcessing(true);

      let filename = file.name;
      const isSupported = !!file.type.match(/^image\/(png|jpeg|svg)/i);
      const isSVG = file.type.startsWith('image/svg');
      const isRaster = !isSVG && !!file.type.match(/^image\//i);
      const needsConversion = !isSupported && !isRaster;

      // Convert all SVGs to PNGs, because they may contain fonts or external references that the
      // client has access to, but which ichabod will not. We provide the config so that the
      // conversion can be done at the maximum size (currently 1.5 times larger in case it has margins).
      if (isSVG && !!config) {
        setMessageAsync('Converting vector to scaled raster...');
        filename = file.name.replace(/[.].+$/, '') + '.png';
        const png = await convertSVGToPNG(file, config);
        file = new File([png.blob], filename);
      // Ichabod uses node-canvas, which only supports PNG and JPEG. So if the file is something
      // else (TIFF and WEBP, most likely), convert it on the client as well.
      } else if (!isSupported && isRaster) {
        setMessageAsync('Converting to supported format...');
        filename = file.name.replace(/[.].+$/, '') + '.png';
        const readImageAsync = (file) => {
          return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onloadend = (event, arg2) => {
              resolve(event.target.result);
            }
            reader.readAsDataURL(file)
          });
        };
        let url = await readImageAsync(file);
        let img = await loadImage(url);
        const png = await convertImageToPNGAsync(url, img.width, img.height);
        file = new File([png.blob], filename);
      }

      if (wantUploadsDeferred && !needsConversion) {
        const reader = new FileReader();
        reader.onload = async ({ target: { result: dataUrl } }) => {
          gNewUploadId += 1;
          onUploaded({ filename: filename, mimetype: file.type, url: dataUrl, deferredDataUrl: dataUrl, id: `_${gNewUploadId}` });
        };
        reader.readAsDataURL(file);
      } else {

        const doUpload = async (uploadUrl, file, linkData) => {
          const response = await uploadImageAsync(uploadUrl, file, linkData);

          const data = Array.isArray(response) ? response[0] : response;

          // check if we converted to an SVG and if so convert to PNG like above
          if (data.mimetype.startsWith('image/svg')) {
            setMessageAsync('Converting vector to scaled raster...');

            const filename = data.filename.replace(/[.].+$/, '') + '.png';
            const blob = await (await fetch(data.url)).blob();
            const png = await convertSVGToPNG(blob, config);
            const converted = new File([png.blob], filename);
            const link = {filename: data.filename, parentId: data.id, linkType: 'png conversion'};

            setMessageAsync('Uploading file...');
            return await doUpload(uploadUrl, converted, { links: [link] });
          } else {
            return data;
          }
        };

        try {
          setMessageAsync(needsConversion ? 'Converting file to SVG...' : 'Uploading file...');
          const data = await doUpload(uploadUrl, file);
          onUploaded(data);
        } catch (e) {
          console.error(e);
          alert('Failed to upload file.');
        }

        setMessageAsync(null);
        setProcessing(false);
      }
    }
  };

  return (
    <div className='upload-design'>
      {!processing && (
        <>
          <span>
            <i className='fa fa-cloud-upload' /> UPLOAD A DESIGN
          </span>
          <input type='file' accept='image/*,.eps,.ai' onChange={handleUpload} />
        </>
      )}
      {processing && <Spinner size='md'>{message || 'Uploading file...'}</Spinner>}
    </div>
  );
};
